import React, { useState, useEffect } from 'react';
import {
  intialPageSize,
  markedAsViewedMessage,
  markedAsUnviewedMessage,
  PaperPropsWithoutScrollbar,
} from 'constants';
import {
  formatDate,
  getSnackBar,
  useNotification,
  getDialogHeading,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import {
  getAdminViewLinkUsers,
  getAdminUnViewLinkUsers,
  updateAdminLinkViewStatus,
  viewLinkEngagement as getViewLinkEngagement,
} from 'layouts/userLinks/services/linksApi';
import { MassBox } from 'components/Ui/styled';
import { useSelector, useDispatch } from 'react-redux';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import {
  setViewUnviewUsers,
  setViewEngagementLists,
} from 'layouts/userLinks/store/adminLinksSlice/adminLinksSlice';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { renderCellExpand } from 'components/Common/GridCell/GridCellExpand';

const EngagementMetricsDialog = (props) => {
  const { open, setOpen } = props;

  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const availableUsersList = useSelector(
    (state) => state?.availableUsersList || []
  );

  const { viewEngagementLinkId: linkId, viewUnviewUsers = [] } = useSelector(
    (state) => state.adminLinks || {}
  );

  const [viewLinkEngagement, setViewLinkEngagement] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
    page: 1,
  });

  useEffect(() => {
    setViewLinkEngagement((prev) => ({
      ...prev,
      rows: getEngagementMetricsRows(viewUnviewUsers, availableUsersList),
    }));
  }, [viewUnviewUsers, availableUsersList]);

  const handleClose = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setOpen(false);
  };

  const getEngagementMetricsRows = (viewUnviewUsers, availableUsersList) => {
    if (!viewUnviewUsers || viewUnviewUsers?.length === 0) return [];

    return viewUnviewUsers.map((user) => {
      const matchedUser = availableUsersList.find(
        (u) => u?.userId === user?.userId
      );

      const displayName = matchedUser
        ? `${matchedUser?.givenName || ''} ${
            matchedUser?.familyName || ''
          }`.trim() || matchedUser?.username
        : 'Unknown';

      return {
        id: user?.id,
        userId: user?.userId,
        userName: displayName,
        name: user?.trainingLink?.name,
        trainingLinkId: user?.trainingLinkId,
        status: user?.status,
        createdAt: formatDate(user?.createdAt),
        updatedAt: formatDate(user?.updatedAt),
      };
    });
  };

  const handleViewUnView = async (params) => {
    const { id, status } = params?.row;
    const payload = {
      status: status === 'viewed' ? 'unviewed' : 'viewed',
    };
    const snackbarMessage =
      status === 'viewed' ? markedAsViewedMessage : markedAsUnviewedMessage;

    try {
      setViewLinkEngagement((prev) => ({
        ...prev,
        isLoading: true,
      }));
      await updateAdminLinkViewStatus(payload, id);
      const getLinkEngagementData = await getViewLinkEngagement(linkId);
      const callApi =
        status === 'viewed'
          ? () => getAdminViewLinkUsers(linkId)
          : () => getAdminUnViewLinkUsers(linkId);

      const data = await callApi();
      dispatch(setViewUnviewUsers(data));
      dispatch(setViewEngagementLists(getLinkEngagementData));

      setViewLinkEngagement((prev) => ({
        ...prev,
        rows: getEngagementMetricsRows(viewUnviewUsers, availableUsersList),
      }));

      dispatch(displaySnackbar(getSnackBar(snackbarMessage)));
      setViewLinkEngagement((prev) => ({
        ...prev,
        isLoading: false,
      }));
    } catch (error) {
      setViewLinkEngagement((prev) => ({
        ...prev,
        isLoading: false,
      }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const columns = [
    {
      field: 'mark',
      headerName: 'Mark',
      width: 80,
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const { status } = params.row;
        const isViewed = status === 'viewed';
        return (
          <>
            <Button
              size="small"
              onClick={() => handleViewUnView(params)}
              data-testid="unViewButton"
            >
              {isViewed ? 'Un-view' : 'View'}
            </Button>
          </>
        );
      },
    },
    {
      headerName: 'Name',
      field: 'name',
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      headerName: 'User Name',
      field: 'userName',
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
    },
    {
      headerName: 'Assigned',
      flex: 1,
      field: 'createdAt',
      renderCell: renderCellExpand,
    },
    {
      headerName: 'Updated',
      flex: 1,
      field: 'updatedAt',
      renderCell: renderCellExpand,
    },
  ];

  const pageSizeChange = pageSizeChangeHandler(setViewLinkEngagement);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={PaperPropsWithoutScrollbar}
      >
        {getDialogHeading('Engagement Metrics', handleClose)}
        <MassBox>
          <DeltaDataGrid
            columns={columns}
            setListData={pageSizeChange}
            listData={viewLinkEngagement}
          />
          <NotificationPopup />
        </MassBox>
      </Dialog>
    </>
  );
};

export default EngagementMetricsDialog;
